// src/PasteData.js
import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Alert,
  Collapse,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import RateLimitDialog from './components/RateLimitDialog';

// Per-field byte cap (matches backend MAX_FIELD_BYTES in app.py).
const MAX_FIELD_BYTES = 100_000;

const measureBytes = (text) => new Blob([text || '']).size;
const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  return `${(n / 1024).toFixed(1)} KB`;
};
const sizeHelperText = (bytes) => {
  if (bytes > MAX_FIELD_BYTES) {
    return `Too large — ${formatBytes(bytes)} (max ${formatBytes(MAX_FIELD_BYTES)})`;
  }
  return `${formatBytes(bytes)} / ${formatBytes(MAX_FIELD_BYTES)}`;
};

function PasteData({ setFlights, breakdownParams }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Flight Number + raw text states
  const [flightNumber, setFlightNumber] = useState('');
  const [tdrText, setTdrText] = useState('');
  const [brockText, setBrockText] = useState('');

  // Dev fixtures can arrive with rebuilt paste text (TDR + bin info) in
  // navigation state — fill the fields as if the dispatcher had pasted.
  useEffect(() => {
    const p = location.state?.pastePrefill;
    if (p) {
      setFlightNumber(p.flightNumber || '');
      setTdrText(p.tdrText || '');
      setBrockText(p.brockText || '');
    }
  }, [location.state]);
  const [freightManifestText, setFreightManifestText] = useState('');
  // Add loading and general error states
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showGeneralError, setShowGeneralError] = useState(false);

  // Rate-limit dialog state. Set by the 429 catch in handleSubmit.
  const [rateLimitOpen, setRateLimitOpen] = useState(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);

  // Live byte sizes for each text field — drive the helperText counter
  // and the over-limit red state on the inputs.
  const tdrBytes = measureBytes(tdrText);
  const brockBytes = measureBytes(brockText);
  const freightManifestBytes = measureBytes(freightManifestText);
  const tdrOversize = tdrBytes > MAX_FIELD_BYTES;
  const brockOversize = brockBytes > MAX_FIELD_BYTES;
  const freightManifestOversize = freightManifestBytes > MAX_FIELD_BYTES;
  // The freight manifest field only counts while its checkbox is checked —
  // otherwise an oversized paste left in the hidden field would silently
  // disable the BREAKDOWN button with no visible red state to explain it.
  // (Submit already sends null when the box is unchecked.)
  const anyOversize =
    tdrOversize || brockOversize ||
    (Boolean(breakdownParams?.hasFreightManifest) && freightManifestOversize);

  // ---------------------------
  // TDR mismatch / success states
  // ---------------------------
  const [tdrErrorMessage, setTdrErrorMessage] = useState('');
  const [tdrOpenError, setTdrOpenError] = useState(false);
  const [tdrSuccessMessage, setTdrSuccessMessage] = useState('');
  const [tdrOpenSuccess, setTdrOpenSuccess] = useState(false);
  const [flightNumberTdrError, setFlightNumberTdrError] = useState(false);
  const [tdrFieldError, setTdrFieldError] = useState(false);
  const [flightNumberTdrSuccess, setFlightNumberTdrSuccess] = useState(false);
  const [tdrFieldSuccess, setTdrFieldSuccess] = useState(false);

  // ---------------------------
  // Brock mismatch / success states
  // ---------------------------
  const [brockErrorMessage, setBrockErrorMessage] = useState('');
  const [brockOpenError, setBrockOpenError] = useState(false);
  const [brockSuccessMessage, setBrockSuccessMessage] = useState('');
  const [brockOpenSuccess, setBrockOpenSuccess] = useState(false);
  const [flightNumberBrockError, setFlightNumberBrockError] = useState(false);
  const [brockFieldError, setBrockFieldError] = useState(false);
  const [flightNumberBrockSuccess, setFlightNumberBrockSuccess] = useState(false);
  const [brockFieldSuccess, setBrockFieldSuccess] = useState(false);

  // ---------------------------
  // Local Parsers
  // ---------------------------
  const parseTdrFlightNumber = (text) => {
    // For TDR lines like "Flight: 3820"
    const match = text.match(/Flight:\s*([A-Za-z0-9]+)/);
    return match ? match[1] : null;
  };
  
  const parseBrockFlightNumber = (text) => {
    // For Brock lines like "WN 3820" or "WN 0777" with leading zero
    const match = text.match(/WN\s+0?(\d+)/);
    return match ? match[1] : null;
  };

  // ---------------------------
  // Clear TDR states
  // ---------------------------
  const clearTdrError = () => {
    setTdrErrorMessage('');
    setTdrOpenError(false);
    setFlightNumberTdrError(false);
    setTdrFieldError(false);
  };
  const clearTdrSuccess = () => {
    setTdrSuccessMessage('');
    setTdrOpenSuccess(false);
    setFlightNumberTdrSuccess(false);
    setTdrFieldSuccess(false);
  };
  const clearAllTdrStates = () => {
    clearTdrError();
    clearTdrSuccess();
  };

  // ---------------------------
  // Clear Brock states
  // ---------------------------
  const clearBrockError = () => {
    setBrockErrorMessage('');
    setBrockOpenError(false);
    setFlightNumberBrockError(false);
    setBrockFieldError(false);
  };
  const clearBrockSuccess = () => {
    setBrockSuccessMessage('');
    setBrockOpenSuccess(false);
    setFlightNumberBrockSuccess(false);
    setBrockFieldSuccess(false);
  };
  const clearAllBrockStates = () => {
    clearBrockError();
    clearBrockSuccess();
  };

  // ---------------------------
  // Checking TDR flight vs typed flight
  // ---------------------------
  const checkTdrMatch = (tdrFltNum, typedFltNum) => {
    if (!tdrFltNum || !typedFltNum) {
      clearAllTdrStates();
      return;
    }
    if (tdrFltNum === typedFltNum) {
      // MATCH
      clearTdrError();
      setTdrSuccessMessage(`TDR Flight Number (${tdrFltNum}) matches Flight Number (${typedFltNum}).`);
      setTdrOpenSuccess(true);
      setFlightNumberTdrSuccess(true);
      setTdrFieldSuccess(true);
    } else {
      // MISMATCH
      clearTdrSuccess();
      setTdrErrorMessage(
        `TDR Flight Number (${tdrFltNum}) does not match Flight Number (${typedFltNum}).`
      );
      setTdrOpenError(true);
      setFlightNumberTdrError(true);
      setTdrFieldError(true);
    }
  };

  // ---------------------------
  // Checking Brock flight vs typed flight
  // ---------------------------
  const checkBrockMatch = (brockFltNum, typedFltNum) => {
    if (!brockFltNum || !typedFltNum) {
      clearAllBrockStates();
      return;
    }
    if (brockFltNum === typedFltNum) {
      // MATCH
      clearBrockError();
      setBrockSuccessMessage(
        `Brock / SmartSuite Flight Number (${brockFltNum}) matches Flight Number (${typedFltNum}).`
      );
      setBrockOpenSuccess(true);
      setFlightNumberBrockSuccess(true);
      setBrockFieldSuccess(true);
    } else {
      // MISMATCH
      clearBrockSuccess();
      setBrockErrorMessage(
        `Brock / SmartSuite Flight Number (${brockFltNum}) does not match Flight Number (${typedFltNum}).`
      );
      setBrockOpenError(true);
      setFlightNumberBrockError(true);
      setBrockFieldError(true);
    }
  };

  // ---------------------------
  // onChange Handlers
  // ---------------------------
  const handleFlightNumberChange = (e) => {
    const newFlightNum = e.target.value;
    setFlightNumber(newFlightNum);

    // Re-check TDR
    const tdrFltNum = parseTdrFlightNumber(tdrText);
    checkTdrMatch(tdrFltNum, newFlightNum);

    // Re-check Brock
    const brockFltNum = parseBrockFlightNumber(brockText);
    checkBrockMatch(brockFltNum, newFlightNum);
  };

  const handleTdrChange = (e) => {
    const newTdr = e.target.value;
    setTdrText(newTdr);

    const tdrFltNum = parseTdrFlightNumber(newTdr);
    checkTdrMatch(tdrFltNum, flightNumber);
  };

  const handleBrockChange = (e) => {
    const newBrock = e.target.value;
    setBrockText(newBrock);

    const brockFltNum = parseBrockFlightNumber(newBrock);
    checkBrockMatch(brockFltNum, flightNumber);
  };

  const handleFreightManifestChange = (e) => {
    setFreightManifestText(e.target.value);
  };

  // ---------------------------
  // Dynamic styling for TDR fields
  // ---------------------------
  const getTdrBorderColor = () => {
    if (tdrOversize) return 'red';
    if (tdrFieldError) return 'red';
    if (tdrFieldSuccess) return 'green';
    return '#e2e2e2';
  };
  const getTdrLabelColor = () => {
    if (tdrOversize) return 'red';
    if (tdrFieldError) return 'red';
    if (tdrFieldSuccess) return 'green';
    return '#e2e2e2';
  };
  const getTdrFlightNumBorderColor = () => {
    if (flightNumberTdrError) return 'red';
    if (flightNumberTdrSuccess) return 'green';
    return '#e2e2e2';
  };
  const getTdrFlightNumLabelColor = () => {
    if (flightNumberTdrError) return 'red';
    if (flightNumberTdrSuccess) return 'green';
    return '#e2e2e2';
  };

  // ---------------------------
  // Dynamic styling for Brock fields
  // ---------------------------
  const getBrockBorderColor = () => {
    if (brockOversize) return 'red';
    if (brockFieldError) return 'red';
    if (brockFieldSuccess) return 'green';
    return '#e2e2e2';
  };
  const getBrockLabelColor = () => {
    if (brockOversize) return 'red';
    if (brockFieldError) return 'red';
    if (brockFieldSuccess) return 'green';
    return '#e2e2e2';
  };
  const getBrockFlightNumBorderColor = () => {
    if (flightNumberBrockError) return 'red';
    if (flightNumberBrockSuccess) return 'green';
    return '#e2e2e2';
  };
  const getBrockFlightNumLabelColor = () => {
    if (flightNumberBrockError) return 'red';
    if (flightNumberBrockSuccess) return 'green';
    return '#e2e2e2';
  };

  // ---------------------------
  // "BREAKDOWN" logic
  // ---------------------------
  const handleSubmit = async () => {
    try {
      // Input validation
      if (!flightNumber.trim()) {
        setGeneralError('Please enter a flight number');
        setShowGeneralError(true);
        return;
      }
      
      if (!tdrText.trim()) {
        setGeneralError('Please paste TDR text');
        setShowGeneralError(true);
        return;
      }
      
      if (!brockText.trim()) {
        setGeneralError('Please paste Brock/SmartSuite text');
        setShowGeneralError(true);
        return;
      }
      
      // Clear any previous errors and set loading state
      setGeneralError('');
      setShowGeneralError(false);
      setLoading(true);
      
      // Log the parameters for debugging
      console.log("Submitting with parameters:", breakdownParams);
      console.log("Sending data to server:", {
        flightNumber,
        tdrText: tdrText.substring(0, 50) + "...", // Truncate for logging
        brockText: brockText.substring(0, 50) + "..." // Truncate for logging
      });
      
      // Make the API call
      const res = await axios.post('/paste-data', {
        flightNumber,
        tdrText,
        brockText,
        freightManifestText: breakdownParams?.hasFreightManifest ? freightManifestText : null
      });

      console.log("Received response:", res);
      
      // Expecting { tdrData: {...}, brockData: {...}, freightManifestData?: {...} }
      if (res.data && res.data.tdrData && res.data.brockData) {
        console.log("Valid data received from server");
        
        // 1) Store in parent state if needed
        if (setFlights) {
          setFlights(res.data);
        }

        // Create a clean copy of the parameters to ensure the boolean value is preserved
        const cleanParams = {
          isPriorityFlight: breakdownParams?.isPriorityFlight === true, // Explicitly convert to boolean
          hasFreightManifest: breakdownParams?.hasFreightManifest === true,
          hotThreshold: parseInt(breakdownParams?.hotThreshold || 30, 10),
          coldThresholdHours: parseFloat(breakdownParams?.coldThresholdHours || 2),
          flightNote: breakdownParams?.flightNote || '' // Note for all drivers
        };

        // 2) Store in localStorage to list in /flights - INCLUDING parameters
        let storedFlights = [];
        try {
          storedFlights = JSON.parse(localStorage.getItem('flightList') || '[]');
          if (!Array.isArray(storedFlights)) storedFlights = [];
        } catch (e) {
          console.warn('Corrupt flightList in localStorage; resetting.', e);
          storedFlights = [];
        }
        storedFlights.push({
          tdrData: res.data.tdrData,
          brockData: res.data.brockData,
          freightManifestData: res.data.freightManifestData || null,
          parseWarnings: res.data.parseWarnings || [],
            brockWarnings: res.data.brockWarnings || [],
            tdrMissingFlights: res.data.tdrMissingFlights || [],
            tdrMissingMass: res.data.tdrMissingMass || false,
          brockWarnings: res.data.brockWarnings || [],
          breakdownParams: cleanParams, // Store parameters along with the flight data
          timestamp: Date.now()
        });
        try {
          localStorage.setItem('flightList', JSON.stringify(storedFlights));
        } catch (storageErr) {
          // Storage quota exceeded — the saved flight list is full. Saving is
          // part of the contract (dispatchers reference past flights from the
          // Flights page), so stop here with clear instructions instead of
          // surfacing a raw browser error or navigating with an unsaved flight.
          console.error('Failed to save flight to flightList:', storageErr);
          setGeneralError(
            'Your saved flight list is full, so this breakdown could not be saved. ' +
            'Open the Flights page, delete old flights (or use Delete All), then paste again.'
          );
          setShowGeneralError(true);
          return;
        }

        // Log the exact parameters being passed to navigation
        console.log("Navigating with cleaned parameters:", cleanParams);
        console.log("isPriorityFlight type:", typeof cleanParams.isPriorityFlight);

        // 3) Navigate to /flight-data with the parameters. Carry ALL the
        // safety-net fields — the board reads these from location.state on
        // a fresh paste; missing ones only surfaced after a page reload.
        navigate('/flight-data', {
          state: {
            tdrData: res.data.tdrData,
            brockData: res.data.brockData,
            freightManifestData: res.data.freightManifestData || null,
            parseWarnings: res.data.parseWarnings || [],
            brockWarnings: res.data.brockWarnings || [],
            tdrMissingFlights: res.data.tdrMissingFlights || [],
            tdrMissingMass: res.data.tdrMissingMass || false,
            breakdownParams: cleanParams
          }
        });
      } else {
        console.error('Invalid response format from server', res.data);
        setGeneralError('Server returned an invalid response format. Check console for details.');
        setShowGeneralError(true);
      }
    } catch (err) {
      console.error('Error calling /paste-data:', err);

      // 429 = rate limit. Show the countdown dialog instead of the
      // generic error alert; gives the dispatcher a clear "wait N s"
      // message rather than a confusing "Server error: 429".
      if (err.response && err.response.status === 429) {
        const retryAfterHeader = err.response.headers?.['retry-after'];
        const seconds = parseInt(retryAfterHeader, 10);
        setRateLimitSeconds(Number.isFinite(seconds) && seconds > 0 ? seconds : 60);
        setRateLimitOpen(true);
        return;
      }

      let errorMessage = 'Error connecting to the server. Is your backend running?';

      if (err.response) {
        console.error('Server responded with:', err.response.data);
        if (err.response.data && err.response.data.error) {
          errorMessage = `Server error: ${err.response.data.error}`;
        } else {
          errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
        }
      } else if (err.request) {
        console.error('No response received from server');
        errorMessage = 'No response received from server. Check if your backend is running.';
      } else {
        console.error('Error setting up request:', err.message);
        errorMessage = `Request error: ${err.message}`;
      }
      
      setGeneralError(errorMessage);
      setShowGeneralError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ textAlign: 'left' }}>
      {/* General Error Alert */}
      <Collapse in={showGeneralError} sx={{ marginBottom: showGeneralError ? '1rem' : 0 }}>
        <Alert
          severity="error"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setShowGeneralError(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {generalError}
        </Alert>
      </Collapse>
    
      {/* TDR Error Alert */}
      <Collapse in={tdrOpenError} sx={{ marginBottom: tdrOpenError ? '1rem' : 0 }}>
        <Alert
          severity="error"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => clearTdrError()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {tdrErrorMessage}
        </Alert>
      </Collapse>

      {/* TDR Success Alert */}
      <Collapse in={tdrOpenSuccess} sx={{ marginBottom: tdrOpenSuccess ? '1rem' : 0 }}>
        <Alert
          severity="success"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => clearTdrSuccess()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {tdrSuccessMessage}
        </Alert>
      </Collapse>

      {/* Brock Error Alert */}
      <Collapse in={brockOpenError} sx={{ marginBottom: brockOpenError ? '1rem' : 0 }}>
        <Alert
          severity="error"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => clearBrockError()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {brockErrorMessage}
        </Alert>
      </Collapse>

      {/* Brock Success Alert */}
      <Collapse in={brockOpenSuccess} sx={{ marginBottom: brockOpenSuccess ? '1rem' : 0 }}>
        <Alert
          severity="success"
          variant="filled"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => clearBrockSuccess()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {brockSuccessMessage}
        </Alert>
      </Collapse>


      {/* Flight Number */}
      <TextField
        label="Flight Number"
        value={flightNumber}
        onChange={handleFlightNumberChange}
        placeholder="Enter flight number (e.g., 3820)"
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={{
          marginBottom: '1rem',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: getTdrFlightNumBorderColor() || getBrockFlightNumBorderColor(),
            },
            '&:hover fieldset': {
              borderColor: getTdrFlightNumBorderColor() || getBrockFlightNumBorderColor(),
            },
            '&.Mui-focused fieldset': {
              borderColor: getTdrFlightNumBorderColor() || getBrockFlightNumBorderColor(),
            },
          },
          '& .MuiInputBase-input': {
            color: '#e2e2e2',
          },
          '& .MuiInputLabel-root': {
            color: getTdrFlightNumLabelColor() || getBrockFlightNumLabelColor(),
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: getTdrFlightNumLabelColor() || getBrockFlightNumLabelColor(),
          },
        }}
      />

      {/* TDR Raw Text */}
      <TextField
        label="TDR"
        multiline
        rows={6}
        value={tdrText}
        onChange={handleTdrChange}
        placeholder="Paste the full TDR here. Include the print time..."
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: true }}
        helperText={sizeHelperText(tdrBytes)}
        FormHelperTextProps={{
          sx: {
            color: tdrOversize ? '#ff6b6b' : 'rgba(255,255,255,0.55)',
            marginLeft: 0,
          },
        }}
        sx={{
          marginBottom: '1rem',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: getTdrBorderColor(),
            },
            '&:hover fieldset': {
              borderColor: getTdrBorderColor(),
            },
            '&.Mui-focused fieldset': {
              borderColor: getTdrBorderColor(),
            },
          },
          '& .MuiInputBase-input': {
            color: '#e2e2e2',
          },
          '& .MuiInputLabel-root': {
            color: getTdrLabelColor(),
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: getTdrLabelColor(),
          },
        }}
      />

      {/* Brock Raw Text */}
      <TextField
        label="Brock / SmartSuite"
        multiline
        rows={6}
        value={brockText}
        onChange={handleBrockChange}
        placeholder="Paste Brock / SmartSuite bin information here."
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: true }}
        helperText={sizeHelperText(brockBytes)}
        FormHelperTextProps={{
          sx: {
            color: brockOversize ? '#ff6b6b' : 'rgba(255,255,255,0.55)',
            marginLeft: 0,
          },
        }}
        sx={{
          marginBottom: '1rem',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: getBrockBorderColor(),
            },
            '&:hover fieldset': {
              borderColor: getBrockBorderColor(),
            },
            '&.Mui-focused fieldset': {
              borderColor: getBrockBorderColor(),
            },
          },
          '& .MuiInputBase-input': {
            color: '#e2e2e2',
          },
          '& .MuiInputLabel-root': {
            color: getBrockLabelColor(),
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: getBrockLabelColor(),
          },
        }}
      />

      {/* Freight Manifest Text - Only show if checkbox is checked */}
      {breakdownParams?.hasFreightManifest && (
        <TextField
          label="Freight Manifest"
          multiline
          rows={6}
          value={freightManifestText}
          onChange={handleFreightManifestChange}
          placeholder="Paste freight manifest data here..."
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
          helperText={sizeHelperText(freightManifestBytes)}
          FormHelperTextProps={{
            sx: {
              color: freightManifestOversize ? '#ff6b6b' : 'rgba(255,255,255,0.55)',
              marginLeft: 0,
            },
          }}
          sx={{
            marginBottom: '1rem',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: freightManifestOversize ? 'red' : '#e2e2e2',
              },
              '&:hover fieldset': {
                borderColor: freightManifestOversize ? 'red' : '#e2e2e2',
              },
              '&.Mui-focused fieldset': {
                borderColor: freightManifestOversize ? 'red' : '#e2e2e2',
              },
            },
            '& .MuiInputBase-input': {
              color: '#e2e2e2',
            },
            '& .MuiInputLabel-root': {
              color: freightManifestOversize ? 'red' : '#e2e2e2',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: freightManifestOversize ? 'red' : '#e2e2e2',
            },
          }}
        />
      )}

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading || anyOversize}
        sx={{
          backgroundColor: '#e2e2e2',
          color: '#000',
          fontWeight: 'bold',
          width: '100%', // full width
          padding: '12px 16px', // Make button taller
          '&:hover': {
            backgroundColor: '#ccc',
          },
          '&.Mui-disabled': {
            backgroundColor: '#a0a0a0',
            color: '#333',
          },
        }}
      >
        {loading ? (
          <>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            PROCESSING...
          </>
        ) : (
          'BREAKDOWN'
        )}
      </Button>

      <RateLimitDialog
        open={rateLimitOpen}
        retryAfterSeconds={rateLimitSeconds}
        onClose={() => setRateLimitOpen(false)}
      />
    </div>
  );
}

export default PasteData;